import { DataObject } from 'src/app/common/models/chatbotexpand.model';

export class DataTransformersHelper {
    static tokenizeSearchText(searchText: string): string[] {
        return searchText.split(/\s+/).filter((token: any) => token.length > 0);
    }

    static matchesAllTokens(text: string, tokens: string[]): boolean {
        const lowerText = text.toLowerCase();
        return tokens.every((token: any) => lowerText.includes(token));
    }

    static filterByVariableCode(variableTabData: any[], variableCode: string, responseCode?: string): any[] {
        return variableTabData.filter(
            (a: any) =>
                a.variable_code === variableCode &&
                (responseCode ? a.response_code === responseCode : true)
        );
    }

    static getDistinctValues<T, K extends keyof T>(list: T[], key: K): T[K][] {
        return [...new Set(list.map((item) => item[key]))];
    }

    static getDistinctLevel0Values(list: any[]) {
        return DataTransformersHelper.getDistinctValues(list, 'Level0');
    }

    static getDistinctLevel1Values(list: any[]) {
        return DataTransformersHelper.getDistinctValues(list, 'Level1');
    }

    static sortByChildIndex(items: any[], referenceData: any[]): void {
        items.sort((a, b) => {
            const indexA = referenceData.findIndex((data: any) => data.Child === a.Child);
            const indexB = referenceData.findIndex((data: any) => data.Child === b.Child);
            return indexA - indexB;
        });
    }

    static isDimensionPresent(list: any[]): boolean {
        return list.some((entry) => entry.is_dimension === true);
    }

    static isLevel1TimePeriodPresent(list: any[]): boolean {
        return list.some((entry) => entry.Level0 === 'Time Period');
    }

    static getDistinctAggregaredMetrics(list: any[]) {
        const distinctLevel0Values = [...new Set(list.map((item) => item.Level0))];
        return distinctLevel0Values;
    }

    static getDistinctENtryList(list: any[]) {
        const distinctLevel0Values = [...new Set(list.map((item) => item.Level1))];
        return distinctLevel0Values;
    }

    static checkBrands(metrics: string[]): number {
        let hasDelimiter = metrics.some((metric) => metric.includes('|'));
        if (!hasDelimiter) return 0;

        let firstParts = metrics.map((metric) => metric.split('|')[0]);
        let uniqueFirstParts = Array.from(new Set(firstParts));

        if (uniqueFirstParts.length === 1) {
            return 1;
        } else if (uniqueFirstParts.every((part) => part[0] === uniqueFirstParts[0][0])) {
            return 2;
        } else {
            return 3;
        }
    }

    static checkMetrics(metrics: string[]): number {
        return DataTransformersHelper.checkBrands(metrics); // Identical logic
    }

    static handlemetricname(metricName: any, type: number = 0) {
        if (type == 0) return metricName;
        else if (type == 1) {
            const parts = metricName.split('|');
            return parts[1];
        }

        const parts = metricName.split('|');
        if (parts.length <= 2) {
            return metricName;
        }

        return parts.slice(-2).join('|').trim();
    }

    static handleBrandName(metricName: any, type: number = 0) {
        return DataTransformersHelper.handlemetricname(metricName, type); // Identical logic
    }

    static getOutputData(comp: any, block: any): any {
    const blockId = block.messageId || 'live-message';
    const blockMcode = block.mcode || comp.getMcode(block) || 0;

    if (block.builtTableData && block.builtTableData.Filter) {
      return block.builtTableData;
    }

    if (block.OutputdataObject && (block.OutputdataObject.Row?.length > 0 || block.OutputdataObject.Column?.length > 0)) {
      if (!(block.OutputdataObject as any).flatData && block.CrossTabOutPutData) {
        (block.OutputdataObject as any).flatData = block.CrossTabOutPutData;
      }
      return block.OutputdataObject;
    }

    if (!block.messageId && block === comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1] && comp.OutputdataObject?.Row?.length > 0) {
      return comp.OutputdataObject;
    }

    const analytical = block.analyticalData || block.fullAnswer || {};

    // Try to find the configuration or use raw analytical data
    let data = analytical.getConfiguration ||
      analytical.getSnapshotOutput ||
      analytical.AgentPayload?.getConfiguration ||
      analytical.AgentPayload ||
      analytical.DataObject ||
      (analytical.Data ? analytical : null) ||
      (analytical.Row ? analytical : null);

    if (!data) {
      return null;
    }

    const dataString = (data?.response?.Data && typeof data.response.Data === 'string') ? data.response.Data :
      ((data?.Data && typeof data.Data === 'string') ? data.Data :
        ((data?.response && typeof data.response === 'string') ? data.response : null));

    const filterExistsInSource = !!(analytical.Filter || data.Filter || analytical.filters || data.filters || analytical.AgentPayload?.filters);
    if (block.builtTableData && (block.builtTableData.Filter || !filterExistsInSource)) {
      if (!block.CrossTabOutPutData && block.builtTableData.flatData) {
        block.CrossTabOutPutData = block.builtTableData.flatData;
      }
      return block.builtTableData;
    }

    if (dataString && !data?.Row) {
      const dataWrapper = data?.response?.Data ? data.response : data;
      const rawData = dataWrapper.Data;
      block.CrossTabOutPutData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      block.builtTableData = comp.buildHistoricalTableData(dataWrapper);

      let combinedFilters: string[] = [];
      const filtersObj = analytical.Filters ||
        data.Filters ||
        data.response?.Filters ||
        analytical.filters ||
        data.filters ||
        data.response?.filters ||
        analytical.AgentPayload?.Filters ||
        analytical.AgentPayload?.filters ||
        analytical.AgentPayload?.getConfiguration?.Filter ||
        analytical.AgentPayload?.getConfiguration?.filters;

      if (filtersObj && typeof filtersObj === 'object') {
        Object.values(filtersObj).forEach((val: any) => {
          if (Array.isArray(val)) {
            combinedFilters.push(...val);
          } else if (typeof val === 'string' && val.trim() !== '') {
            combinedFilters.push(val);
          }
        });
      }

      const filterSource = analytical.Filter ||
        data.Filter ||
        data.response?.Filter ||
        (combinedFilters.length > 0 ? combinedFilters : null) ||
        analytical.Filters ||
        data.Filters ||
        analytical.filters ||
        data.filters;

      const rowNestingSource = analytical.RowNesting || data.RowNesting || data.response?.RowNesting ||
        analytical.row_nesting || data.row_nesting || data.Row_Nesting ||
        analytical.AgentPayload?.row_nesting || analytical.AgentPayload?.RowNesting || analytical.Row_Nesting;

      const colNestingSource = analytical.ColNesting || data.ColNesting || data.response?.ColNesting ||
        analytical.column_nesting || data.column_nesting || data.Column_Nesting ||
        analytical.AgentPayload?.column_nesting || analytical.AgentPayload?.ColNesting || analytical.Column_Nesting;

      const rowSource = analytical.Row || data.Row || analytical.row || data.row ||
        analytical.AgentPayload?.row || analytical.AgentPayload?.Row;

      const colSource = analytical.Column || data.Column || analytical.column || data.column ||
        analytical.AgentPayload?.column || analytical.AgentPayload?.Column;

      if (filterSource) {
        block.builtTableData.Filter = comp.transformToDataObject(filterSource, 'Filter');
      }
      if (rowNestingSource) {
        block.builtTableData.RowNesting = comp.transformToDataObject(rowNestingSource, 'Row Nesting');
      }
      if (colNestingSource) {
        block.builtTableData.ColNesting = comp.transformToDataObject(colNestingSource, 'Column Nesting');
      }

      if (block.builtTableData.Row?.length === 0 && rowSource) {
        block.builtTableData.Row = comp.transformToDataObject(rowSource, 'Row');
      }
      if (block.builtTableData.Column?.length === 0 && colSource) {
        block.builtTableData.Column = comp.transformToDataObject(colSource, 'Column');
      }

      return block.builtTableData;
    }

    const finalData = {
      ...data,
      Row: data?.Row || data?.row || [],
      Column: data?.Column || data?.column || [],
      RowNesting: data?.RowNesting || data?.row_nesting || data?.Row_Nesting || [],
      ColNesting: data?.ColNesting || data?.column_nesting || data?.Column_Nesting || []
    };

    if (!block.CrossTabOutPutData || block.CrossTabOutPutData.length === 0) {
      const rawData = data?.Data || data?.response?.Data;
      if (rawData) {
        block.CrossTabOutPutData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      }
    }

    if (finalData.Row.length === 0) {
      console.warn(`[getOutputData] ⚠️ Final table data has 0 rows for block: ${block.messageId}`);
    }

    if (!block.OutputdataObject || block.OutputdataObject.Row?.length === 0) {
      block.OutputdataObject = finalData;
    }

    if (!(block.OutputdataObject as any).flatData && block.CrossTabOutPutData) {
      (block.OutputdataObject as any).flatData = block.CrossTabOutPutData;
    }
    if (!(finalData as any).flatData && block.CrossTabOutPutData) {
      (finalData as any).flatData = block.CrossTabOutPutData;
    }

    return finalData;
  }

  static transformToDataObject(comp: any, arr: any, fallbackType: string): any[] {
    let actualArr: string[] = [];
    if (Array.isArray(arr)) {
      actualArr = arr;
    } else if (typeof arr === 'string' && arr.trim() !== '') {
      actualArr = [arr];
    }

    if (actualArr.length === 0) return [];

    const groups: { [key: string]: string[] } = {};
    actualArr.forEach((item: any) => {
      const parts = item.split('|');
      const parent = parts.length > 1 ? parts[0] : fallbackType;
      if (!groups[parent]) groups[parent] = [];
      groups[parent].push(item);
    });

    return Object.keys(groups).map((parent: any) => ({
      Parent: parent,
      SortId: 0,
      Data: groups[parent].map((item, index): any => {
        const parts = item.split('|');
        let normalizedChild: string;
        if (parts.length >= 3 && parts[0] === parts[1]) {
          normalizedChild = parts.slice(1).join('|');
        } else if (parts.length >= 3 && parts[0] === parent) {
          normalizedChild = parts.slice(1).join('|');
        } else {
          normalizedChild = item;
        }
        return {
          CheckboxStatus: true,
          Child: normalizedChild,
          variablecode: '',
          responsecode: '',
          isDerviedVariable: false,
          CalculationType: 'share',
          sort_id: index + 1,
          year_response_code: '',
          year_variable_code: '',
          Date: '',
          isMultiPunch: 0,
          isTimePeriod: ['time', 'year', 'month', 'quarter', 'mma'].some((k: any) => parent.toLowerCase().includes(k)),
          isMarket: parent.toLowerCase().includes('market'),
          multiPunchMetric: '',
          MultiPunchNumber: 0,
          isCustomGroup: false,
          kibanaFilterScript: '',
          customGroupId: '',
          isRolling: false,
          isCustomCalculation: false,
          customCalculationId: ''
        };
      }),
      isMultiPunch: false,
      MultiPunchNumber: 0,
      isCustomGroup: false,
      customGroupId: '',
      isCustomCalculation: false,
      customCalculationId: '',
      brandanalysisCode: (comp.mcode === 2) ? 1 : 0,
      is_entrypoint: false
    }));
  }


  static getLastLevelColumnNestingValues(comp: any, block?: any) {
    var indexData;
    const outputDataObj = block ? comp.getOutputData(block) : comp.OutputdataObject;
    if (outputDataObj && outputDataObj.Column && outputDataObj.Column.length > 0) {
      if (outputDataObj['Column'][0]['Data'].length > 0) {
        let firstLevelLength =
          outputDataObj['Column'][0]['Data'].length;

        if (firstLevelLength > 0) {
          let level0 =
            outputDataObj['Column'][0]['Data'][firstLevelLength - 1];
          if (level0.NestedArray.length > 0) {
            let secondLevelLength = level0.NestedArray.length;
            if (secondLevelLength > 0) {
              let level1 = level0.NestedArray[secondLevelLength - 1];

              if (level1.NestedArray.length > 0) {
                let finalData =
                  outputDataObj['Column'][0]['Data'][
                    firstLevelLength - 1
                  ].NestedArray[secondLevelLength - 1].NestedArray;
                indexData = finalData;
                if (!block) comp.columnLength = 2;
              } else {
                indexData = level0.NestedArray;
                if (!block) comp.columnLength = 1;
              }
            }
          } else {
            indexData = outputDataObj['Column'][0]['Data'];
            if (!block) comp.columnLength = 0;
          }
        }
      }
    }

    return indexData;
  }

  static sortTrendTimeporiodData(comp: any, metricData: any[]): any[] {
    let filteredLeftPanelData = comp._configService.leftPanelData?.filter(
      (item: any) =>
        item.Level0 === 'Time Period' || item.Level1 === 'Time Period'
    );
    filteredLeftPanelData?.sort((a: any, b: any) => {
      return a.level1_sort_id - b.level1_sort_id;
    });

    metricData?.sort((a: any, b: any) => {
      const indexA = filteredLeftPanelData?.findIndex(
        (item: any) => item.MetricOption === a.lvl1Child.split('|')[1]
      );
      const indexB = filteredLeftPanelData?.findIndex(
        (item: any) => item.MetricOption === b.lvl1Child.split('|')[1]
      );
      return indexA - indexB;
    });
    metricData?.sort((a: any, b: any) => {
      const [monthA, yearA] = a.lvl1Child?.split('|')[1].split('_');
      const [monthB, yearB] = b.lvl1Child?.split('|')[1].split('_');
      const dateA: any = new Date(yearA, comp.getMonthNumber(monthA) - 1);
      const dateB: any = new Date(yearB, comp.getMonthNumber(monthB) - 1);
      return dateA - dateB;
    });

    return metricData;
  }

  static isWaveTimePeriod(comp: any) {
    if (
      comp.mandatoryVariableMapping == undefined ||
      comp.mandatoryVariableMapping == null
    )
      return false;
    else {
      let timePeriodEntries: any = comp.mandatoryVariableMapping.filter(
        (entry: any) =>
          entry.variable_label === 'Time Period' &&
          entry.variable_code &&
          entry.variable_code.trim() !== ''
      );

      let isWaveTimePeriod =
        timePeriodEntries.length === 0 ||
        timePeriodEntries.every(
          (entry: any) =>
            entry.variable_code === timePeriodEntries[0].variable_code
        );

      return isWaveTimePeriod;
    }
  }

  static AssignDataToPreviousDataObject(comp: any, dataObject: DataObject) {
    comp.previewDataObject.Row = dataObject.Row;
    comp.previewDataObject.Column = dataObject.Column;
    comp.previewDataObject.ColNesting = dataObject.ColNesting;
    comp.previewDataObject.RowNesting = dataObject.RowNesting;
    comp.previewDataObject.Filter = dataObject.Filter;
  }

  static cleanBrandAnalysisChild(comp: any, obj: any): void {
    if (!obj || typeof obj !== 'object') return;

    if (Array.isArray(obj)) {
      for (const item of obj as any) {
        comp.cleanBrandAnalysisChild(item);
      }
    } else {
      const childKeys = ['Child', 'rowChild', 'colChild', 'Level1RowChild', 'Level2RowChild', 'Level1ColChild', 'Level2ColChild', 'Parent'];
      for (const key of childKeys) {
        if (typeof obj[key] === 'string') {
          if (obj[key].endsWith('|Yes')) {
            obj[key] = obj[key].substring(0, obj[key].length - 4);
          } else if (obj[key].endsWith('|No')) {
            obj[key] = obj[key].substring(0, obj[key].length - 3);
          }
        }
      }
      for (const key of Object.keys(obj)) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          comp.cleanBrandAnalysisChild(obj[key]);
        }
      }
    }
  }

  static normalizeNestedChildFields(comp: any, data: any[]): void {
    if (!data?.length) return;
    const normalize = (val: string) => {
      if (!val) return val;
      const p = val.split('|');
      return p.length >= 3 ? p.slice(1).join('|') : val;
    };
    data.forEach((item: any) => {
      if (item.Level1RowChild) item.Level1RowChild = normalize(item.Level1RowChild);
      if (item.Level2RowChild) item.Level2RowChild = normalize(item.Level2RowChild);
      if (item.Level1ColChild) item.Level1ColChild = normalize(item.Level1ColChild);
      if (item.Level2ColChild) item.Level2ColChild = normalize(item.Level2ColChild);
      if (item.rowChild) item.rowChild = normalize(item.rowChild);
      if (item.colChild) item.colChild = normalize(item.colChild);
    });
  }
}
